import { Badge, Modal, Spinner, Toggle } from '@/components/ui'
import { QueryErrorState } from '@/components/common/QueryErrorState'
import {
  useCreateLeaveType,
  useLeaveTypes,
  useUpdateLeaveType,
} from '@/features/leave/hooks/useLeaveTypes'
import { LeaveTypeForm } from './LeaveTypeForm'

interface LeaveTypesModalProps {
  open: boolean
  onClose: () => void
}

/** Manage the organization's leave types and whether each one is paid. */
export function LeaveTypesModal({
  open,
  onClose,
}: LeaveTypesModalProps): React.ReactElement | null {
  const typesQuery = useLeaveTypes({ includeInactive: true, enabled: open })
  const createMutation = useCreateLeaveType()
  const updateMutation = useUpdateLeaveType()

  if (!open) return null

  const types = typesQuery.data ?? []

  return (
    <Modal open={open} onClose={onClose} title="Leave types" className="max-w-xl">
      <div className="space-y-6">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Payroll deducts approved days of unpaid leave types. Changes apply to payroll runs
          calculated from now on; paid runs do not change.
        </p>
        {typesQuery.isLoading ? (
          <div className="flex justify-center py-6">
            <Spinner />
          </div>
        ) : typesQuery.isError ? (
          <QueryErrorState error={typesQuery.error} onRetry={typesQuery.refetch} />
        ) : (
          <ul className="divide-y divide-gray-100 dark:divide-gray-800">
            {types.map((type) => (
              <li key={type._id} className="flex flex-wrap items-center gap-3 py-3">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                    {type.label}
                  </p>
                  <div className="mt-1 flex gap-1.5">
                    <Badge variant={type.isPaid ? 'success' : 'warning'}>
                      {type.isPaid ? 'Paid' : 'Unpaid'}
                    </Badge>
                    {!type.isActive ? <Badge variant="neutral">Inactive</Badge> : null}
                  </div>
                </div>
                <Toggle
                  size="sm"
                  checked={type.isPaid}
                  onChange={(isPaid) =>
                    updateMutation.mutate({ leaveTypeId: type._id, body: { isPaid } })
                  }
                  disabled={updateMutation.isPending}
                  label="Paid"
                />
                <Toggle
                  size="sm"
                  checked={type.isActive}
                  onChange={(isActive) =>
                    updateMutation.mutate({ leaveTypeId: type._id, body: { isActive } })
                  }
                  disabled={updateMutation.isPending}
                  label="Active"
                />
              </li>
            ))}
          </ul>
        )}
        <div className="border-t border-gray-100 pt-4 dark:border-gray-800">
          <LeaveTypeForm
            onSubmit={(body) => createMutation.mutate(body)}
            isSubmitting={createMutation.isPending}
          />
        </div>
      </div>
    </Modal>
  )
}

LeaveTypesModal.displayName = 'LeaveTypesModal'
